import { Add, ChevronRight } from '@mui/icons-material';
import {
    Autocomplete,
    Box,
    Button,
    Skeleton,
    Stack,
    type SxProps,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    type Theme,
    Typography,
} from '@mui/material';
import { sortBy, uniqBy } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import useUnitProvider from '/@/core/context/unit';
import { useCatalogProductsByBrands, useCatalogProductsById } from '/@/hooks/catalog';
import { useToggle } from '/@/hooks/toggle/useToggle';
import { _ } from '/@/locale';
import { getCatalogBrandsByCrop } from '/@/store/catalog/selectors';
import { getSessionCountry } from '/@/store/session/selectors';
import { reverse } from '/@/utils/router';
import Dots, { References } from '../components/dots';
import { parseProductForProductCard } from '../components/product-card';
import { ProductComparatorModal } from '../components/product-comparator';
import { useProductComparator } from '../hooks/product-comparator';
import { ALL_BRANDS_SLUG } from '../list';

export type Feature = {
    label: string;
    value: string | number;
    productId: number;
    id: number;
};

type Metric = Feature & {
    value: number;
};

export const generateMatrix = (items: Feature[] | Metric[], component: (value: number | string) => React.ReactNode) => {
    const matrix: Record<number, Record<number, React.ReactNode>> = {};
    for (const { productId, id, value } of items) {
        if (!matrix[id]) matrix[id] = {};
        matrix[id][productId] = component(value);
    }
    return matrix;
};

export const generateUniqueItems = <T extends Feature | Metric>(items: T[], key: keyof T): T[] =>
    sortBy(
        uniqBy(items, (el) => el[key]),
        ['order']
    );

const renderTableCell = (
    featureId: number,
    productId: number,
    matrix: Record<number, Record<number, React.ReactNode>>,
    isEven: boolean
) => (
    <TableCell key={`${featureId}-${productId}`} sx={[isEven && { background: '#F5F5F5' }]}>
        {matrix[featureId]?.[productId]}
    </TableCell>
);

const renderProductRow = (
    feature: Feature | Metric,
    products: any[],
    matrix: Record<number, Record<number, React.ReactNode>>
) => (
    <TableRow key={feature.id}>
        <TableCell>
            <Typography variant='caption' fontWeight='bold' color='primary'>
                {feature.label}
            </Typography>
        </TableCell>
        {products.map((product, i) => renderTableCell(feature.id, product.id, matrix, i % 2 === 0))}
    </TableRow>
);

const ProductAutocomplete = ({
    id,
    onChange,
    selectedProducts,
}: {
    id: number;
    onChange: (id: number) => void;
    selectedProducts: Set<number>;
}) => {
    const units = useUnitProvider();
    const country = useSelector(getSessionCountry);
    const { type: crop } = useParams();
    const cropBrands = useSelector((state) => getCatalogBrandsByCrop(state, crop || 'corn'));
    const productsQuery = useCatalogProductsByBrands(
        cropBrands,
        {
            crop: crop || 'corn',
            country: country?.code || 'AR',
            page: 1,
            pageSize: 10000,
        },
        { enabled: cropBrands.length > 0 }
    );

    const filteredProducts = useMemo(
        () =>
            parseProductForProductCard(
                (productsQuery.data?.results || [])
                    .filter((p) => Boolean(_(p.catalogZone) && (!selectedProducts.has(p.id) || p.id === id)))
                    .map((p) => ({ ...p, catalogType: 'corn', slugLabel: p.brand.name.toLowerCase() })),
                units
            ),
        [productsQuery.data?.results, selectedProducts]
    );

    return (
        <Autocomplete
            options={filteredProducts || []}
            value={
                filteredProducts.find((p) => p.id === id) || {
                    catalogName: '',
                }
            }
            isOptionEqualToValue={(opt, v) => opt.id === v.id}
            getOptionLabel={(option) => option?.catalogName}
            onChange={(event, newValue) => (newValue ? onChange(newValue.id) : onChange(id))}
            renderTags={() => null}
            disableCloseOnSelect
            renderOption={(props, option) => (
                <Box {...props} key={option.id}>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <Typography variant='subtitle2'>{option.catalogName || option.name}</Typography>
                        {option.chips?.map((chip) => {
                            return chip;
                        })}
                    </Stack>
                </Box>
            )}
            renderInput={(params) => <TextField {...params} variant='outlined' />}
            disableClearable={selectedProducts.size <= 2}
            sx={{
                '& .MuiAutocomplete-endAdornment': {
                    '& .MuiButtonBase-root': {
                        width: '24px',
                        height: '24px',
                    },
                },
            }}
        />
    );
};

const ProductCard = ({
    id,
    catalogType,
    slugLabel,
    name,
    chips,
    description,
    onChange,
    selectedProducts,
}: {
    id: number;
    name: string;
    description: string;
    chips?: { text: string; sx?: SxProps<Theme> };
    catalogType: string;
    slugLabel: string;
    onChange: (id: number) => void;
    selectedProducts: Set<number>;
}) => {
    const navigate = useNavigate();

    return (
        <Stack sx={{ border: (t) => `1px solid ${t.palette.divider}`, height: '100%' }}>
            <Stack p={2} spacing={2}>
                <ProductAutocomplete id={id} onChange={(id) => onChange(id)} selectedProducts={selectedProducts} />

                <Stack direction='row' spacing={1} mb={2} useFlexGap flexWrap='wrap'>
                    {(chips || []).map((chip) => {
                        return chip;
                    })}
                </Stack>
                <Typography variant='body2' color='primary' mb={'auto'} sx={{ overflow: 'auto' }}>
                    {description}
                </Typography>
            </Stack>
            <Stack
                direction={{ sm: 'row' }}
                justifyContent='space-between'
                alignItems='center'
                mt={'auto !important'}
                p={2}
                sx={{
                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                }}
            >
                <Button
                    variant='contained'
                    onClick={() => {
                        navigate(
                            reverse('productExploration.detail', {
                                params: { type: catalogType, slug: slugLabel, productId: id },
                            })
                        );
                    }}
                >
                    {_('Recomendar')}
                </Button>
            </Stack>
        </Stack>
    );
};

const ProductComparator = () => {
    const units = useUnitProvider();
    const navigate = useNavigate();
    const { open, setOpen, setClose } = useToggle();

    const { selectedProducts, resetAndSelectMultipleProducts } = useProductComparator();

    const { catalogType, productIds } = useParams();
    const productIdsArray = useMemo(() => productIds?.split(',').map(Number) || [], [productIds]);
    useEffect(() => {
        resetAndSelectMultipleProducts(productIdsArray);
    }, []);
    useEffect(() => {
        if (selectedProducts.size > 0)
            navigate(
                reverse('productExploration.comparator', {
                    params: { catalogType, productIds: [...selectedProducts].join(',') },
                }),
                {
                    replace: true,
                }
            );
    }, [selectedProducts]);

    const catalogQuery = useCatalogProductsById({ ids: [...selectedProducts], crop: catalogType });
    const parsedProducts = useMemo(() => {
        if (['corn', 'soy'].includes(catalogType)) {
            return parseProductForProductCard(
                (catalogQuery.data || []).filter((p) => Boolean(_(p.catalogZone))).map((p) => ({ ...p, catalogType })),
                units
            );
        }
        return parseProductForProductCard(
            (catalogQuery.data || []).map((p) => ({ ...p, catalogType })),
            units
        );
    }, [catalogQuery.data]);

    const features = useMemo<Feature[]>(
        () =>
            (catalogQuery.data || []).flatMap((p) =>
                (p?.features || []).map((el) => ({
                    label: _(el.name),
                    value: _(el.value),
                    productId: p.id,
                    id: el.id,
                }))
            ),
        [catalogQuery.data]
    );
    const featuresMatrix = useMemo(
        () =>
            generateMatrix(features, (value) => (
                <Typography variant='body2' color='primary'>
                    <ReactMarkdown>{String(value)}</ReactMarkdown>
                </Typography>
            )),
        [features]
    );
    const uniqueFeatures = useMemo(() => generateUniqueItems(features, 'id'), [features]);

    const metrics = useMemo<Metric[]>(
        () =>
            (catalogQuery.data || []).flatMap((p) =>
                (p?.metrics || []).map((el) => ({
                    label: _(el.name),
                    value: el.value,
                    productId: p.id,
                    id: el.id,
                }))
            ),
        [catalogQuery.data]
    );
    const metricsMatrix = useMemo(
        () => generateMatrix(metrics, (value) => <Dots numberOfDots={9} selected={Number(value)} />),
        [metrics]
    );
    const uniqueMetrics = useMemo(() => generateUniqueItems(metrics, 'id'), [metrics]);

    const [tab, setTab] = useState<keyof typeof tabs>('features');
    const tabs = {
        features: {
            label: _('Características'),
            enabled: uniqueFeatures.length > 0,
        },
        metrics: {
            label: _('Perfil defensivo'),
            enabled: uniqueMetrics.length > 0,
        },
    };

    return (
        <PageContainer
            header={
                <Header
                    title={_('Revisá las diferencias entre productos')}
                    subtitle={_('Información comparativa de los híbridos según su perfil agronómico y defensivo.')}
                    breadcrumbs={[
                        { label: _('Productos'), onClick: () => navigate(reverse('productExploration.main')) },
                        {
                            label: _('Híbridos'),
                            onClick: () =>
                                navigate(
                                    reverse('productExploration.list', {
                                        params: { slug: ALL_BRANDS_SLUG, type: catalogType },
                                    })
                                ),
                        },
                        { label: _('Comparación de productos') },
                    ]}
                />
            }
        >
            <Stack spacing={5} pt={5}>
                <Stack spacing={3}>
                    <Stack
                        spacing={2}
                        direction='row'
                        sx={{
                            overflowX: 'auto',
                        }}
                        maxWidth='100%'
                    >
                        {catalogQuery.isValidating ? (
                            [...Array(4)].map((_, i) => (
                                <Stack key={`skeleton-${i}`} width='25%' minWidth='200px'>
                                    <Skeleton variant='rectangular' height='300px' />
                                </Stack>
                            ))
                        ) : (
                            <>
                                {parsedProducts?.map(
                                    (
                                        {
                                            imageSrc,
                                            name,
                                            description,
                                            id,
                                            chips,
                                            technologies,
                                            typeLabel,
                                            catalogType,
                                            slugLabel,
                                            newProduct,
                                        },
                                        i
                                    ) => {
                                        return (
                                            <Stack key={id} width='25%' minWidth='200px'>
                                                <ProductCard
                                                    id={id}
                                                    imageSrc={imageSrc}
                                                    name={name}
                                                    description={description}
                                                    typeLabel={typeLabel}
                                                    chips={chips}
                                                    technologies={technologies}
                                                    catalogType={catalogType}
                                                    slugLabel={slugLabel}
                                                    newProduct={newProduct || false}
                                                    onChange={(newId) => {
                                                        let newSelectedProducts = [...selectedProducts];
                                                        const index = newSelectedProducts.indexOf(id);

                                                        if (newId === id) {
                                                            newSelectedProducts = newSelectedProducts.filter(
                                                                (pId) => pId !== newId
                                                            );
                                                        } else {
                                                            if (index !== -1) {
                                                                newSelectedProducts[index] = newId;
                                                            } else {
                                                                newSelectedProducts.push(newId);
                                                            }
                                                        }

                                                        resetAndSelectMultipleProducts(newSelectedProducts);
                                                    }}
                                                    selectedProducts={selectedProducts}
                                                />
                                            </Stack>
                                        );
                                    }
                                )}
                                {[...Array(4 - parsedProducts.length)].map((el, i) => (
                                    <Stack
                                        key={i}
                                        sx={{
                                            border: (t) => `1px dashed ${t.palette.divider}`,
                                        }}
                                        direction='row'
                                        spacing={0.5}
                                        width='25%'
                                        minWidth='200px'
                                        alignItems='center'
                                        justifyContent='center'
                                    >
                                        <Button
                                            color='primary'
                                            variant='text'
                                            startIcon={
                                                <Add
                                                    sx={{
                                                        color: 'primary.onSurface',
                                                    }}
                                                />
                                            }
                                            onClick={setOpen}
                                        >
                                            {_('Agregar un híbrido')}
                                        </Button>
                                    </Stack>
                                ))}
                            </>
                        )}
                    </Stack>

                    <Box>
                        <Tabs
                            textColor='secondary'
                            indicatorColor='secondary'
                            value={tab}
                            variant={'fullWidth'}
                            onChange={(_, value) => setTab(value)}
                            sx={{ borderTop: 'none' }}
                        >
                            {Object.entries(tabs)
                                .filter(([_, t]) => t.enabled)
                                .map(([tab, { label }]) => (
                                    <Tab key={tab} iconPosition='start' value={tab} label={label} />
                                ))}
                        </Tabs>

                        <Box
                            sx={{
                                overflowX: 'auto',
                            }}
                            maxWidth='100%'
                        >
                            <Table
                                sx={{
                                    '& .MuiTableRow-root': {
                                        '&:hover': { background: 'inherit' },
                                        background: 'white',
                                        backgroundColor: 'white !important',
                                    },
                                    borderTop: (t) => `1px solid ${t.palette.divider}`,
                                    mt: 0.5,
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell />
                                        {(catalogQuery.data || []).map((product, i) => (
                                            <TableCell key={product.id} sx={[i % 2 === 0 && { background: '#F5F5F5' }]}>
                                                <Typography variant='subtitle1' fontWeight='bold' color='primary'>
                                                    {product.catalogName || product.name}
                                                </Typography>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {(tab === 'features' ? uniqueFeatures : uniqueMetrics).map((feature) =>
                                        renderProductRow(
                                            feature,
                                            catalogQuery.data || [],
                                            tab === 'features' ? featuresMatrix : metricsMatrix
                                        )
                                    )}
                                    <TableRow
                                        sx={{
                                            '& .MuiTableCell-root': { borderBottom: 'none' },
                                        }}
                                    >
                                        <TableCell />
                                        {(catalogQuery.data || []).map((product, i) => (
                                            <TableCell key={product.id} sx={[i % 2 === 0 && { background: '#F5F5F5' }]}>
                                                <Button
                                                    variant='text'
                                                    endIcon={<ChevronRight />}
                                                    onClick={() => {
                                                        navigate(
                                                            reverse('productExploration.detail', {
                                                                params: { type: catalogType, productId: product.id },
                                                            })
                                                        );
                                                    }}
                                                >
                                                    {_('Ver detalle')}
                                                </Button>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </Box>
                    </Box>
                    {tab === 'metrics' && (
                        <References
                            numberOfDots={9}
                            sx={{
                                border: (t) => `1px solid ${t.palette.divider}`,
                                py: 2,
                                px: 3,
                            }}
                        />
                    )}
                </Stack>
            </Stack>
            <ProductComparatorModal
                open={open}
                setClose={setClose}
                selectedProducts={selectedProducts}
                resetAndSelectMultipleProducts={resetAndSelectMultipleProducts}
            />
        </PageContainer>
    );
};

export default ProductComparator;
